import type { ResolvedCollection } from '@rstore/shared';
import type { Collection, CollectionDefaults, FindOptions, StoreSchema, VueQueryPage, VueQueryReturn, VueStore } from '@rstore/vue';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, onScopeDispose, ref, toValue, watch } from 'vue';
import { isDeepEqual } from '#layers/base/public';

type PaginatedQuery<TItem> = VueQueryReturn<
	Collection,
	CollectionDefaults,
	StoreSchema,
	FindOptions<Collection, CollectionDefaults, StoreSchema>,
	TItem[]
>;

type PaginatedQueryPage<TItem> = Pick<
	VueQueryPage<
		Collection,
		CollectionDefaults,
		StoreSchema,
		FindOptions<Collection, CollectionDefaults, StoreSchema>,
		TItem[]
	>,
	'data' | 'loading' | 'completed' | 'error'
>;

/** RStore query API used by the pagination controller. */
export type PaginatedQuerySource<TItem> = Pick<
	PaginatedQuery<TItem>,
	'pages' | 'data' | 'loading' | 'meta'
> & {
	getPage: (args: any) => PaginatedQueryPage<TItem> | undefined;
	fetchMore: (args: any) => unknown;
	refresh: () => PromiseLike<unknown>;
};

/** RStore store API used to subscribe to collection mutations. */
export type PaginatedQueryStore = Pick<VueStore, '$hooks'>;

interface UsePaginatedQueryOptions<TItem> {
	/** Store that owns the query and its mutation hooks. */
	store: PaginatedQueryStore;
	/** The RStore query whose pages should be exposed. */
	collectionQuery: PaginatedQuerySource<TItem>;
	/** Writable one-based page number shared with the pagination UI. */
	pageNumber: Ref<number>;
	/** Reactive number of items requested per page. */
	pageSize: MaybeRefOrGetter<number>;
	/** Reactive collection API name used to match mutation hooks. */
	collectionApiName: MaybeRefOrGetter<string | undefined>;
	/** Whether page fetching and loading state are enabled. */
	enabled?: MaybeRefOrGetter<boolean>;
	/** Optional reactive query state that resets the page when it changes. */
	resetOn?: MaybeRefOrGetter<unknown>;
	/** Whether resetOn changes should currently reset the page. */
	resetEnabled?: MaybeRefOrGetter<boolean>;
}

interface MutationPayload {
	collection: Pick<ResolvedCollection, 'name'>;
}

interface UsePaginatedQueryReturn<TItem> {
	/** Items on the selected page. */
	items: ComputedRef<TItem[]>;
	/** Server-provided total, or `undefined` while the total is unknown. */
	totalItemsCount: ComputedRef<number | undefined>;
	/** Logical item count through the highest page that has loaded rows. */
	currentItemsCount: ComputedRef<number>;
	/** Whether the selected page is waiting for data. */
	isLoading: ComputedRef<boolean>;
}

/**
 * Adds shared one-based pagination behavior to an RStore paginated query.
 */
export function usePaginatedQuery<TItem>(
	options: UsePaginatedQueryOptions<TItem>,
): UsePaginatedQueryReturn<TItem> {
	const { collectionQuery, pageNumber, pageSize, collectionApiName, enabled, resetOn, resetEnabled, store } = options;

	const isEnabled = computed(() => enabled === undefined || toValue(enabled));
	const computedPageSize = computed(() => toValue(pageSize));
	const currentPageIndex = computed(() => pageNumber.value - 1);
	const currentPageStartIndex = computed(() => currentPageIndex.value * computedPageSize.value);

	const currentPage = computed(() => collectionQuery.getPage({ pageIndex: currentPageIndex.value }));
	const items = computed<TItem[]>(() => currentPage.value?.data ?? []);
	const pageFetchPending = computed(() => collectionQuery.loading.value || currentPage.value?.loading === true);

	const currentItemsCount = computed(() => collectionQuery.pages.value.reduce((maxCount, page, pageIndex) => {
		const itemCount = page?.data.length ?? 0;
		if (itemCount === 0) return maxCount;
		return Math.max(maxCount, pageIndex * computedPageSize.value + itemCount);
	}, 0));

	const totalItemsCount = computed(() => {
		const totalCount = collectionQuery.meta.value?.totalCount;

		// RStore can briefly retain metadata from the previous query options even
		// after the replacement page has loaded. Never expose an impossible total.
		if (totalCount !== undefined && totalCount < currentItemsCount.value) return;
		return totalCount;
	});

	const canFetchMore = computed(() => {
		if (!isEnabled.value) return false;

		if (currentPageIndex.value === 0) return false;

		const page = currentPage.value;
		if (page?.loading) return false;
		if (page?.completed && !page.error) return false;

		return true;
	});

	// Fetch the current page on demand for pages beyond the first. Page 1
	// (index 0) is owned by rstore's main page. Non-main pages are fetched only
	// when rstore has not completed them yet, or when the previous attempt
	// failed and the user navigates back to that page.
	watch(
		[currentPage, isEnabled],
		() => {
			if (!canFetchMore.value) return;

			collectionQuery.fetchMore({ pageIndex: currentPageIndex.value });
		},
		{ immediate: true },
	);

	const isLoading = computed(() => {
		if (!isEnabled.value) return false;
		if (pageFetchPending.value) return true;
		if (currentPageIndex.value === 0 || currentPage.value?.error) return false;
		if (!currentPage.value || currentPage.value.completed === false) return true;

		const totalCount = totalItemsCount.value;
		return totalCount !== undefined
			&& currentPageStartIndex.value < totalCount
			&& items.value.length === 0;
	});

	watch(computedPageSize, () => {
		if (resetEnabled !== undefined && !toValue(resetEnabled)) return;
		pageNumber.value = 1;
	}, { flush: 'sync' });

	if (resetOn !== undefined) {
		// Default (batched) flush so transient same-tick changes (e.g. a store
		// action clearing then rebuilding an identical filter) compare the settled
		// snapshots instead of resetting the page on every intermediate write.
		watch(() => toValue(resetOn), (newValue, oldValue) => {
			if (resetEnabled !== undefined && !toValue(resetEnabled)) return;
			if (isDeepEqual(newValue, oldValue)) return;
			pageNumber.value = 1;
		});
	}

	const currentPageSettled = computed(() =>
		!pageFetchPending.value
		&& !currentPage.value?.error
		&& currentPage.value?.completed !== false,
	);

	// A total from the previous request can remain visible while RStore refreshes.
	// Waiting for the selected page to settle prevents that stale value from
	// moving the user before the new response has established its real total.
	watch(
		[isEnabled, pageNumber, totalItemsCount, computedPageSize, currentPageSettled],
		() => {
			if (!isEnabled.value || !currentPageSettled.value) return;

			const totalCount = totalItemsCount.value;
			if (totalCount === undefined) return;

			const lastPage = Math.max(1, Math.ceil(totalCount / computedPageSize.value));
			if (pageNumber.value > lastPage) pageNumber.value = lastPage;
		},
		{ immediate: true, flush: 'sync' },
	);

	const mutationRefreshPending = ref(false);

	// A many mutation can emit several single-item hooks plus one aggregate hook.
	// Post-flush scheduling coalesces that synchronous burst, while retaining one
	// follow-up refresh when a genuinely new mutation arrives during a request.
	watch(
		[mutationRefreshPending, () => collectionQuery.loading.value, isEnabled],
		([pending, loading, enabled]) => {
			if (!enabled) {
				mutationRefreshPending.value = false;
				return;
			}

			if (!pending || loading) return;

			mutationRefreshPending.value = false;
			collectionQuery.refresh();
		},
		{ flush: 'post' },
	);

	/** Invalidates server-defined page membership after a relevant mutation. */
	function afterMutation({ collection }: MutationPayload) {
		const isRelevantCollection = collection.name === toValue(collectionApiName);

		if (!isEnabled.value || !isRelevantCollection) {
			return;
		}

		mutationRefreshPending.value = true;
	}

	const offAfterMutation = store.$hooks.hook('afterMutation', afterMutation);
	const offAfterManyMutation = store.$hooks.hook('afterManyMutation', afterMutation);

	onScopeDispose(() => {
		offAfterMutation();
		offAfterManyMutation();
	});

	return {
		items,
		totalItemsCount,
		currentItemsCount,
		isLoading,
	};
}
